import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { setCookie } from '../utils/cookies';
import '../global-design-system.css';
import './PublicRegistration.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

function PublicRegistration() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  // Check URL parameters
  const searchParams = new URLSearchParams(window.location.search);
  const openCamera = searchParams.get('openCamera') === 'true';
  const role = searchParams.get('role') || 'attendee'; // 'attendee' or 'photographer'
  
  const [step, setStep] = useState('loading'); // loading, form, camera, processing, success, error
  const [event, setEvent] = useState(null);
  
  // Form data
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Camera/photo data
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [stream, setStream] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Error message
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch event info
  useEffect(() => {
    fetchEventInfo();
  }, [eventId]);

  const fetchEventInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/public`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data.event);
        setStep('form');
      } else {
        setErrorMessage('Event not found');
        setStep('error');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setErrorMessage('Failed to load event information');
      setStep('error');
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Please enter your name');
      return;
    }
    
    if (!phone.trim()) {
      alert('Please enter your phone number');
      return;
    }
    
    // Basic phone validation - just check it has digits
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    
    setStep('camera');
    startCamera();
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 1280, height: 720 },
        audio: false
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraError(null);
    } catch (error) {
      console.error('Camera access error:', error);
      setCameraError('Unable to access camera. Please grant camera permission and try again.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;
        setCapturedPhotos(prev => [...prev, base64data]);
        
        if (capturedPhotos.length + 1 >= 3) {
          // All 3 photos captured
          stopCamera();
          setStep('processing');
          submitRegistration([...capturedPhotos, base64data]);
        } else {
          setCurrentPhotoIndex(capturedPhotos.length + 1);
        }
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.9);
  };

  const handleCaptureClick = () => {
    // Start countdown
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(timer);
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const retakePhoto = (index) => {
    const newPhotos = [...capturedPhotos];
    newPhotos.splice(index, 1);
    setCapturedPhotos(newPhotos);
    setCurrentPhotoIndex(index);
    startCamera();
    setStep('camera');
  };

  const submitRegistration = async (photos) => {
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim(),
          facePhotos: photos,
          role: role // 'attendee' or 'photographer'
        })
      });

      const data = await response.json();

      if (response.ok || data.alreadyRegistered) {
        // Handle already registered users
        if (data.alreadyRegistered) {
          if (role === 'photographer' && data.token) {
            // Photographer already registered - auto-login
            localStorage.setItem('photographerToken', data.token);
            setCookie('photographerToken', data.token); // For iOS PWA
            console.log('🔑 Photographer already registered - auto-login');
            
            // Redirect immediately to photographer page
            window.location.href = `/photographer/${eventId}`;
            return;
          } else if (role === 'attendee') {
            // Attendee already registered - show friendly message
            setErrorMessage(data.message);
            setStep('success');
            return;
          }
        }
        
        // Store photographer token if provided (new registration)
        if (data.token && role === 'photographer') {
          localStorage.setItem('photographerToken', data.token);
          setCookie('photographerToken', data.token); // For iOS PWA
          console.log('Photographer token stored');
        }
        
        if (openCamera && role === 'photographer') {
          // Redirect to photographer page
          setTimeout(() => {
            window.location.href = `/photographer/${eventId}`;
          }, 1500);
        } else if (openCamera) {
          // Attendee with openCamera - redirect to event photos page
          navigate(`/events/${eventId}/photos?autoCamera=true`);
        } else {
          setStep('success');
        }
      } else {
        setErrorMessage(data.error || 'Registration failed');
        setStep('error');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrorMessage('Registration failed. Please try again.');
      setStep('error');
    }
  };

  const handleSkipFaceRegistration = () => {
    stopCamera();
    setStep('processing');
    submitRegistration([]); // Submit with no photos
  };

  const handleStartOver = () => {
    setCapturedPhotos([]);
    setCurrentPhotoIndex(0);
    setName('');
    setEmail('');
    setPhone('');
    setErrorMessage('');
    setStep('form');
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  if (step === 'loading') {
    return (
      <div className="public-registration-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="public-registration-container">
        <div className="registration-card">
          <h1>❌ Error</h1>
          <p className="error-message">{errorMessage}</p>
          <button className="btn-primary" onClick={handleStartOver}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="public-registration-container">
        <div className="registration-card success-card">
          <div className="success-icon">✅</div>
          <h1>You're All Set!</h1>
          <p className="success-message">
            Thank you for registering for <strong>{event?.name}</strong>!
          </p>
          <p className="success-details">
            You'll receive your photos via text message when they're ready.
          </p>
          <button className="btn-primary" onClick={() => window.location.reload()}>
            Register Another Person
          </button>
        </div>
      </div>
    );
  }

  if (step === 'form') {
    return (
      <div className="public-registration-container">
        <div className="registration-card">
          <h1>📸 Event Registration</h1>
          <h2 className="event-name">{event?.name}</h2>
          <p className="registration-intro">
            Register to receive your event photos via text message!
          </p>
          
          <form onSubmit={handleFormSubmit} className="registration-form">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email (Optional)</label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                className="form-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                required
              />
              <small className="form-hint">
                US numbers only. We'll automatically add +1 and send your photos via text.
              </small>
            </div>
            
            <button type="submit" className="btn-primary btn-full">
              Continue to Face Registration →
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'camera') {
    return (
      <div className="public-registration-container camera-view">
        <div className="camera-card">
          <h2>Take Selfie {currentPhotoIndex + 1} of 3</h2>
          <p className="camera-instructions">
            {currentPhotoIndex === 0 && "Look straight at the camera"}
            {currentPhotoIndex === 1 && "Turn your head slightly to the left"}
            {currentPhotoIndex === 2 && "Turn your head slightly to the right"}
          </p>
          
          {cameraError ? (
            <div className="camera-error">
              <p>{cameraError}</p>
              <button className="btn-secondary" onClick={startCamera}>
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="camera-preview">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="video-preview"
                />
                {countdown && (
                  <div className="countdown-overlay">
                    <div className="countdown-number">{countdown}</div>
                  </div>
                )}
              </div>
              
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <button
                  className="btn-primary btn-capture"
                  onClick={handleCaptureClick}
                  disabled={countdown !== null}
                >
                  📸 Capture Photo
                </button>
                
                <button
                  className="btn-secondary"
                  onClick={handleSkipFaceRegistration}
                  disabled={countdown !== null}
                >
                  Skip Face Registration
                </button>
              </div>
              
              {capturedPhotos.length > 0 && (
                <div className="photo-thumbnails">
                  <p>Captured photos:</p>
                  <div className="thumbnails-grid">
                    {capturedPhotos.map((photo, index) => (
                      <div key={index} className="thumbnail-item">
                        <img src={photo} alt={`Selfie ${index + 1}`} />
                        <button
                          className="btn-retake"
                          onClick={() => retakePhoto(index)}
                        >
                          Retake
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="public-registration-container">
        <div className="registration-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <h2>Processing Registration...</h2>
            <p>{role === 'photographer' ? 'Redirecting to camera...' : 'This may take a few moments'}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default PublicRegistration;
